import { HttpErrorResponse } from '@angular/common/http';

export interface ApiErrorDetails {
  status: number;
  message: string;
  userMessage: string;
  requestId?: string;
  suggestedAction?: string;
}

const DEFAULT_MESSAGES: Record<number, string> = {
  0: 'Connection problem. Check your network and try again.',
  400: 'The request could not be processed. Please review your input and try again.',
  403: 'You don\'t have permission to perform this action.',
  404: 'The requested resource was not found.',
  408: 'The request timed out. Please try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our side. Please try again.',
  502: 'Service temporarily unavailable. Please try again.',
  503: 'Service temporarily unavailable. Please try again.',
  504: 'The service took too long to respond. Please try again.',
};

const SUGGESTED_ACTIONS: Record<number, string> = {
  0: 'Try again',
  403: 'Contact support',
  429: 'Try again',
  500: 'Try again',
  502: 'Try again',
  503: 'Try again',
  504: 'Try again',
};

export function mapHttpErrorToUserMessage(error: HttpErrorResponse): ApiErrorDetails {
  const status = error.status ?? 0;
  const requestId = error.headers?.get('X-Request-Id') ?? error.headers?.get('x-request-id') ?? undefined;

  const apiMessage =
    typeof error.error === 'object' && error.error !== null
      ? (error.error as { error?: string; message?: string }).error ?? (error.error as { message?: string }).message
      : undefined;

  const userMessage =
    DEFAULT_MESSAGES[status] ?? (status >= 500 ? DEFAULT_MESSAGES[503] : 'An unexpected error occurred. Please try again.');

  return {
    status,
    message: apiMessage ?? error.message ?? 'Unknown error',
    userMessage,
    requestId,
    suggestedAction: SUGGESTED_ACTIONS[status] ?? (status >= 500 ? 'Try again' : undefined),
  };
}

export function shouldShowGlobalApiError(status: number): boolean {
  if (status === 401) {
    return false;
  }
  return status === 0 || status === 403 || status >= 408;
}

export function isRetryableClientError(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

export function parseRetryAfterSeconds(error: HttpErrorResponse): number | undefined {
  const header = error.headers?.get('Retry-After');
  if (!header) {
    return undefined;
  }
  const seconds = parseInt(header, 10);
  return Number.isNaN(seconds) ? undefined : seconds;
}
